import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams, Link } from "react-router-dom";

import { CAP, useSession } from "@/auth/session";
import { assetCatalog, organisations } from "@/api/client";
import { ImageField } from "@/components/ImageCapture";
import { SearchInput, Select } from "@/components/inputs";
import { useProducts } from "@/modules/products/data";
import { ScopeAction, ScopeField } from "@/modules/products/ScopeBar";

import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { StatGrid } from "@/components/StatGrid";
import {
  ROLE_LABEL, orgQk, useManufacturerFacet, useOrgUsers, useOrganisation,
  type AllocationRow, type OrgUserRow,
} from "@/modules/organisations/data";

/** One organisation: what it holds and who works in it.
 *
 *  REAL DATA. This page rendered `detail_fixtures.ts`, which is where "Nexcell
 *  line 2" came from — a space in a company that does not exist, allocated to
 *  a product ("Warranty Insights") that does not exist either.
 *
 *  AN ALLOCATION is `(company, product, space)`: the thing a person is
 *  actually granted, and the thing a tool enters. The product is a catalogue
 *  KIND since D-92, and the space belongs to that kind (D-89) — so what
 *  differs between companies is never WHICH spaces exist, only which they
 *  hold and what may be done in them.
 *
 *  404 MEANS BOTH "no such organisation" AND "not yours", deliberately. A
 *  distinct answer would tell a customer that CMP-014 is real.
 */
const ALLOC_COLS: DataCol<AllocationRow>[] = [
  { key: "kind", label: "Product", width: 140, mono: true, render: (a) => a.kind },
  { key: "space", label: "Space", width: 180, render: (a) => a.custom_name ?? a.space },
  { key: "slug", label: "Slug", width: 160, mono: true, render: (a) => a.space_slug },
  {
    key: "users", label: "People with access", width: 150,
    render: (a) => a.users_with_access,
  },
  {
    key: "enabled", label: "State", width: 110,
    render: (a) => a.enabled
      ? <Chip tone="ok">enabled</Chip>
      : <Chip tone="mute">disabled</Chip>,
  },
];

const USER_COLS: DataCol<OrgUserRow>[] = [
  { key: "name", label: "Name", width: 170, render: (u) => u.name },
  { key: "login", label: "Login", width: 230, mono: true, render: (u) => u.login_id },
  { key: "role", label: "Role", width: 140, render: (u) => ROLE_LABEL[u.role] ?? u.role },
  {
    key: "mfa", label: "MFA", width: 80,
    render: (u) => u.mfa ? <Chip tone="ok">on</Chip> : <Chip tone="warn">off</Chip>,
  },
  {
    key: "access", label: "Space access", width: 220,
    render: (u) => u.space_access.length
      ? u.space_access.map((a) => `${a.kind}/${a.space}`).join(", ")
      : <span className="cap">none</span>,
  },
];

export function OrganisationDetailPage() {
  const { orgId = "" } = useParams();
  const q = useOrganisation(orgId);
  const people = useOrgUsers(orgId);

  if (q.isLoading) {
    return <div className="mpage on"><div className="panel panel-pad cap">Loading…</div></div>;
  }
  if (q.isError) {
    return (
      <div className="mpage on">
        <div className="panel panel-pad">
          No such organisation, or it is not one you may see.{" "}
          <Link to="/organisations/organisations">Back to organisations</Link>
        </div>
      </div>
    );
  }

  const o = q.data!;
  const users = people.data?.users ?? [];

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Organisations"
        title={o.name}
        desc={`${o.organisation_id} · ${o.plan}`}
      />

      <div className="panel panel-pad" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
          <KV k="Organisation ID" v={o.organisation_id} mono />
          <KV k="Plan" v={o.plan} />
          <KV k="Status" v={o.status} />
        </div>
      </div>

      <StatGrid stats={[
        { k: "Allocations", v: o.allocations.length },
        { k: "People", v: users.length },
        { k: "With MFA", v: users.filter((u) => u.mfa).length },
      ]} />

      <ManufacturerFacet cid={o.organisation_id} />

      <h3 className="cap" style={{ marginTop: 16 }}>Allocated spaces</h3>
      <DataTable rows={o.allocations} cols={ALLOC_COLS} rowKey={(a) => a.cps_id}
        empty={<div className="panel panel-pad cap">
          This organisation holds no product spaces yet.
        </div>} />

      <h3 className="cap" style={{ marginTop: 16 }}>People</h3>
      <DataTable rows={users} cols={USER_COLS} rowKey={(u) => u.user_id}
        empty={<div className="panel panel-pad cap">
          {people.isLoading ? "Loading…" : "Nobody in this organisation yet."}
        </div>} />
    </div>
  );
}


/** The MAKER facet of this organisation (D-80, D-93).
 *
 *  An organisation IS a party. `manufacturers` holds only what is specific
 *  to MAKING things — abbreviation, what it supplies, country — and D-93
 *  retires the separate Manufacturers sub-page because one organisation
 *  edited in two places is how the live data ended up with "Aphelion Energy
 *  Pvt Ltd" in one table and "Aphelion Energy" in the other.
 *
 *  THERE IS NO NAME FIELD HERE, deliberately. The name comes from the party
 *  above. Offering one would recreate exactly the split this removes.
 *
 *  `supplies` GATES WHICH MODELS this maker may appear on, which is why it
 *  is a checklist of real products rather than free text: a typo produces a
 *  manufacturer that silently never shows up in the picker it was made for,
 *  and that reads as a broken picker.
 */
function ManufacturerFacet({ cid }: { cid: string }) {
  const { can } = useSession();
  // TWO capabilities, because this panel spans two modules. Editing the
  // facet's own fields is MANAGE_ORGANISATIONS; the logo and the archive
  // are CATALOGUE writes — they call `assetCatalog.*`, which is gated on
  // MANAGE_CATALOG. Checking only the first would offer buttons that 403 to
  // anyone holding one and not the other. Both are super-admin-only today,
  // so nobody would hit it — which is exactly why it needed a test rather
  // than a reviewer.
  const mayAuthor = can(CAP.MANAGE_ORGANISATIONS);
  const mayCatalogue = can(CAP.MANAGE_CATALOG);
  const q = useManufacturerFacet(cid);
  const prods = useProducts();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [abbr, setAbbr] = useState("");
  const [country, setCountry] = useState("");
  const [supplies, setSupplies] = useState<string[]>([]);

  const facet = q.data;
  const isMaker = !!facet?.manufacturer_id;

  const open = () => {
    setAbbr(facet?.abbr ?? "");
    setCountry(facet?.country ?? "");
    setSupplies(facet?.supplies ?? []);
    setEditing(true);
  };

  const archive = useMutation({
    mutationFn: () => assetCatalog.archiveManufacturer(facet!.manufacturer_id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
      void qc.invalidateQueries({ queryKey: orgQk.list });
    },
  });

  const save = useMutation({
    mutationFn: () => organisations.setManufacturer(cid, {
      abbr: abbr.trim() || null, country: country.trim() || null, supplies,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
      void qc.invalidateQueries({ queryKey: orgQk.list });
      setEditing(false);
    },
  });

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <h3 className="cap" style={{ margin: 0 }}>Manufacturer</h3>
        {isMaker
          ? <Chip tone="info">{facet?.models ?? 0} model{facet?.models === 1 ? "" : "s"}</Chip>
          : <Chip tone="mute">not a manufacturer</Chip>}
        {mayAuthor && !editing && (
          <button className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={open}>
            {isMaker ? "Edit maker details" : "Make this a manufacturer"}
          </button>
        )}
      </div>

      {!editing && isMaker && (
        <div className="panel panel-pad" style={{ marginTop: 6, display: "flex",
          gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          {facet?.logo_at && (
            <img src={assetCatalog.logoUrl(facet.manufacturer_id!, facet.logo_at)}
              alt="" style={{ width: 40, height: 40, objectFit: "contain" }} />
          )}
          <KV k="Abbreviation" v={facet?.abbr ?? "—"} mono />
          <KV k="Country" v={facet?.country ?? "—"} />
          <KV k="Supplies" v={facet?.supplies.length
            ? facet.supplies.join(", ") : "nothing yet"} />
          {mayCatalogue && (
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {/* ImageField (U-shared) — the same control the catalogue's
                  own manufacturer form uses, pointed at the same endpoints.
                  Hand-rolling a second uploader here would be the review
                  failure CLAUDE.md names, and a worse one than the original. */}
              <ImageField camera={false} size={64} fit="contain" empty="no logo"
                src={facet?.logo_at
                  ? assetCatalog.logoUrl(facet.manufacturer_id!, facet.logo_at)
                  : null}
                onUpload={async (b) => {
                  await assetCatalog.uploadLogo(facet!.manufacturer_id!, b);
                  await qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
                }}
                onRemove={async () => {
                  await assetCatalog.deleteLogo(facet!.manufacturer_id!);
                  await qc.invalidateQueries({ queryKey: orgQk.manufacturer(cid) });
                }} />
              {/* Archive is REFUSED while the maker still has models — the
                  server says so ("has N models — retire those first"), and
                  that refusal is the point: archiving a maker whose models
                  are still buildable would hide it from the picker those
                  models need. */}
              <button className="btn ghost sm" disabled={(facet?.models ?? 0) > 0
                || archive.isPending}
                title={(facet?.models ?? 0) > 0
                  ? "retire its models first" : "hide it from pickers"}
                onClick={() => archive.mutate()}>
                Archive maker
              </button>
            </span>
          )}
        </div>
      )}

      {!editing && !isMaker && (
        <div className="panel panel-pad cap" style={{ marginTop: 6 }}>
          This organisation does not make anything. A manufacturer is a facet
          of a party (D-80) — the same row, with maker-only fields — so it is
          added here rather than created as a second company.
        </div>
      )}

      {editing && (
        <div className="panel panel-pad" style={{ marginTop: 6, display: "flex",
          gap: 14, alignItems: "flex-start", flexWrap: "wrap" }}>
          <ScopeField label="Abbreviation"
            hint="prefixes technical model names — unique across makers">
            <SearchInput value={abbr} mono placeholder="AE" style={{ width: 90 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAbbr(e.target.value.toUpperCase())} />
          </ScopeField>
          <ScopeField label="Country" hint="two letters">
            <SearchInput value={country} mono placeholder="IN" style={{ width: 70 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCountry(e.target.value.toUpperCase().slice(0, 2))} />
          </ScopeField>
          <ScopeField label="Supplies"
            hint="which products it may appear on — a wrong one hides it from that picker">
            <Select value="" style={{ minWidth: 220 }}
              onChange={(e) => {
                const k = e.target.value;
                if (k && !supplies.includes(k)) setSupplies([...supplies, k]);
              }}>
              <option value="">Add a product…</option>
              {(prods.data?.products ?? [])
                .filter((p) => !supplies.includes(p.kind))
                .map((p) => <option key={p.kind} value={p.kind}>{p.name}</option>)}
            </Select>
          </ScopeField>
          <div style={{ flexBasis: "100%", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {supplies.map((k) => (
              <button key={k} className="btn ghost sm"
                onClick={() => setSupplies(supplies.filter((x) => x !== k))}>
                {k} ✕
              </button>
            ))}
            {supplies.length === 0 && (
              <span className="cap">
                Supplies nothing yet — it will not appear on any model picker.
              </span>
            )}
          </div>
          <ScopeAction>
            <span style={{ display: "flex", gap: 8 }}>
              <button className="btn teal sm" disabled={save.isPending}
                onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button className="btn ghost sm" onClick={() => setEditing(false)}>
                Cancel
              </button>
            </span>
          </ScopeAction>
          {save.isError && (
            <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
              {(save.error as Error).message}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
